import { motion } from "framer-motion";
import { useLang } from "../../context/LanguageContext";
import { selectedProjects } from "../../data/content";
import { ArrowUpRight } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const SelectedProjects = () => {
  const { isAr, t } = useLang();
  return (
    <section
      id="projects"
      className="relative bg-bg-ivory py-[120px] md:py-[180px]"
      data-testid="projects-section"
    >
      <div className="max-w-[1760px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex justify-between items-center text-brand-green/65 mb-16">
          <span className="page-meta">— Selected Work</span>
          <span className="page-meta">{t("مشاريع مختارة", "Selected Projects")}</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease }}
          className="mb-20 max-w-3xl"
        >
          <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-green/55 mb-5`}>
            {t("[ مشاريع مختارة ]", "[ PORTFOLIO ]")}
          </div>
          <h3
            className={`${isAr ? "arabic-title" : "serif-title"} text-brand-green mb-6`}
            style={{ fontSize: isAr ? "clamp(42px, 5vw, 76px)" : "clamp(48px, 5.6vw, 104px)" }}
          >
            {isAr ? "أعمال مختارة" : (<>Selected <em className="font-light">Projects</em></>)}
          </h3>
          {isAr ? (
            <p className="arabic-title text-brand-green/75 max-w-xl" dir="rtl" style={{ fontSize: 16, lineHeight: 1.95, fontWeight: 400 }}>
              مشاريع نفّذناها بإتقان لشركاء كبار في المملكة — حيث تتحوّل المواصفات الدقيقة إلى مساحات تعيش طويلاً.
            </p>
          ) : (
            <p className="text-brand-green/75 max-w-xl" style={{ fontSize: 16, lineHeight: 1.8 }}>
              Projects we executed with precision for major partners across the Kingdom — where specifications become spaces that endure.
            </p>
          )}
        </motion.div>

        <div className="editorial-rule mb-12" />

        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {selectedProjects.map((p, i) => {
            const heights = ["h-[460px] md:h-[640px]", "h-[460px] md:h-[540px]", "h-[460px] md:h-[560px]", "h-[460px] md:h-[680px]"];
            const cols = ["col-span-12 md:col-span-7", "col-span-12 md:col-span-5", "col-span-12 md:col-span-5", "col-span-12 md:col-span-7"];
            const offsetTop = i % 2 === 1 ? "md:mt-24" : "";
            return (
              <motion.a
                key={p.code}
                href="#contact"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1.2, ease, delay: (i % 2) * 0.15 }}
                className={`${cols[i]} ${offsetTop} group cursor-hover block`}
                data-testid={`project-${p.code}`}
              >
                <div className={`relative overflow-hidden ${heights[i]}`}>
                  <img
                    src={p.image}
                    alt={isAr ? p.ar_title : p.en_title}
                    className="w-full h-full object-cover transition-transform duration-[1600ms] group-hover:scale-105"
                    style={{ filter: "saturate(0.92)" }}
                  />
                  <div
                    className="absolute inset-0 transition-opacity duration-700 opacity-0 group-hover:opacity-100"
                    style={{ background: "linear-gradient(180deg, rgba(15,47,41,0.05), rgba(15,47,41,0.5))" }}
                  />
                  <div className="absolute top-5 left-5 right-5 flex justify-between text-bg-ivory opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                    <span className="page-meta">— {p.code}</span>
                    <ArrowUpRight size={18} className="flip-x" />
                  </div>
                </div>
                <div className="mt-5 flex flex-col md:flex-row md:items-end justify-between gap-3">
                  <div>
                    <div
                      className={`${isAr ? "arabic-title" : "serif-title italic font-light"} text-brand-green`}
                      style={{ fontSize: isAr ? 26 : 30 }}
                    >
                      {isAr ? p.ar_title : p.en_title}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-green/65`}>
                      {isAr ? p.ar_meta : p.en_meta}
                    </div>
                    <div className="page-meta text-brand-green/55 mt-1">{p.year}</div>
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SelectedProjects;
